import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import AdminSpendWidget from "@/components/AdminSpendWidget";
import AdminGrantTierWidget from "@/components/AdminGrantTierWidget";
import AdminQAGeneratorWidget from "@/components/AdminQAGeneratorWidget";
import AdminStorageWidget from "@/components/AdminStorageWidget";
import UnverifiedEmailBanner from "@/components/UnverifiedEmailBanner";
import BoostPackWidget from "@/components/BoostPackWidget";
import SubscriptionCard from "@/components/SubscriptionCard";
import { Link, useSearchParams } from "react-router-dom";

export default function Account() {
 const { user } = useAuth();
 const [params] = useSearchParams();
 const autoOpenBoost = params.get("boost") === "1" || !!params.get("boost_session_id");
 if (!user) return null;

 // Internal tier ids → user-facing plan names. Matches the /pricing page so
 // a Compass-tier user doesn't see "Copilot" labelled here.
 const TIER_LABELS = { free: "Free", vault: "Vault", copilot: "Compass", dispute: "Dispute Mode", pro: "Pro" };
 const planLabel = TIER_LABELS[user.tier] || user.tier;

 const pct = (used, limit) => Math.min(100, Math.round((used / Math.max(1, limit)) * 100));

 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-3xl mx-auto px-6 py-12">
 <UnverifiedEmailBanner />
 <div className="label-mono mb-2">Your account</div>
 <h1 className="font-serif text-4xl text-[#1A1A1A] mb-8">{user.name}</h1>

 <div className="card mb-6" data-testid="account-info">
 <div className="grid sm:grid-cols-2 gap-6">
 <div><div className="label-mono mb-1">Email</div><div className="text-[#1A1A1A]">{user.email}</div></div>
 <div><div className="label-mono mb-1">Current plan</div><div className="text-[#1A1A1A] font-serif text-lg" data-testid="account-plan-label">{planLabel}</div></div>
 </div>
 </div>

 <div className="card mb-6" data-testid="account-usage">
 <h3 className="font-serif text-xl mb-4">Usage this period</h3>
 <div className="mb-5">
 <div className="flex justify-between text-sm mb-1.5">
 <span className="text-[#333]">AI queries</span>
 <span className="font-mono text-[#6B7280]">{user.ai_queries_used} / {user.ai_queries_limit}</span>
 </div>
 <div className="h-1.5 bg-[#E5E4DE] rounded-full overflow-hidden">
 <div className="h-full bg-[#0D2B52]" style={{ width: `${pct(user.ai_queries_used, user.ai_queries_limit)}%` }} />
 </div>
 </div>
 <div>
 <div className="flex justify-between text-sm mb-1.5">
 <span className="text-[#333]">Storage</span>
 <span className="font-mono text-[#6B7280]">{user.storage_mb_used} MB / {user.storage_mb_limit} MB</span>
 </div>
 <div className="h-1.5 bg-[#E5E4DE] rounded-full overflow-hidden">
 <div className="h-full bg-[#0D2B52]" style={{ width: `${pct(user.storage_mb_used, user.storage_mb_limit)}%` }} />
 </div>
 </div>
 </div>

 <Link to="/pricing" className="btn-primary inline-block" data-testid="upgrade-link">Change plan</Link>

 <div className="mt-8">
 <SubscriptionCard user={user} />
 </div>

 <div className="mt-6" data-testid="account-boost-section">
 <BoostPackWidget autoOpen={autoOpenBoost} userTier={user.tier} />
 </div>

 <div className="mt-8">
 <AdminQAGeneratorWidget />
 <AdminGrantTierWidget />
 <AdminSpendWidget />
 <AdminStorageWidget />
 </div>
 </div>
 </div>
 );
}
